import { defineComponent } from "@digitalbranch/app";

import { emulator_compile, emulator_on_compiled, emulator_on_device, emulator_on_error, emulator_on_exit, emulator_on_memory_changed, emulator_on_ready, emulator_on_registers_changed, emulator_on_stdout, emulator_run, emulator_step, emulator_stop, MemoryChanged, RegistersChanged } from './emulator/emulator'
import { codeEditor } from "../code-editor/code-editor";

import css from './main.css'
import template from './main.html'

type AppMainState = {
  doc: string,
  hasFocus: boolean,
  isRunning: boolean,
  isStepping: boolean
  isCompiled: boolean
  registers: Array<{ name: string, contents: string, ascii: string }>,
  memory: Array<{ address: string, contents: string, ascii: string }>,
  logs: Array<any>,
  programName: string,
  tab: number,
  isSideNavOpen: boolean
  iconFolder: 'folder' | 'folder_open'
  colorSchemeIcon: 'light_mode' | 'dark_mode',
  colorScheme: 'dark' | 'light',
  programs: Array<{
    value: string,
    label: string
  }>
}

export const appMain = defineComponent({
  name: 'app-main',
  css,
  template,
  components: {
    codeEditor
  },
  data(): AppMainState {
    return {
      doc: '',
      hasFocus: false,
      isRunning: false,
      isStepping: false,
      isCompiled: false,
      registers: [],
      memory: [],
      logs: [],
      programName: 'function',
      tab: 1,
      colorSchemeIcon: 'light_mode',
      colorScheme: 'dark',
      isSideNavOpen: true,
      iconFolder: 'folder_open',
      programs: [
        {
          value: 'function',
          label: 'function.s'
        },
        {
          value: 'loop',
          label: 'loop.s'
        },
        {
          value: 'stack',
          label: 'stack.s'
        },
        {
          value: 'pixel_buffer',
          label: 'pixel_buffer.s'
        },
      ],
    }
  },
  listeners: {
    async ready() {
      const { $refs: { pixelBuffer, codeEditor } } = this

      if (!pixelBuffer || !codeEditor) return

      const ctx = (pixelBuffer as HTMLCanvasElement).getContext('2d')

      this.memoryMap = new Map()

      emulator_on_device((event) => {
        const { name, data } = event

        if (name === 'pixel_buffer') {
          if (!ctx) return

          const { red, green, blue, x, y } = data

          if (ctx) {
            ctx.fillStyle = `rgb(${red} ${green} ${blue})`;
            ctx.fillRect(x, y, 1, 1);
          }
        }
      })
      emulator_on_compiled(this.handleCompiled.bind(this))
      emulator_on_stdout(this.handleStdout.bind(this))
      emulator_on_exit(this.handleExit.bind(this))
      emulator_on_error(this.handleError.bind(this))
      emulator_on_registers_changed(this.handleRegistersChanged.bind(this))
      emulator_on_memory_changed(this.handleMemoryChanged.bind(this))
      emulator_on_ready(() => this.logs.push('Emulator ready'))

      this.setProgram((await import('./programs/function')).default)

      codeEditor.focus()
    }
  },
  methods: {
    async handleTabChange(tab: number) {
      const currentTab = this.tab

      this.tab = tab

      if (tab === 1) {
        if(currentTab !== 1) await this.$nextTick()

        this.setEditorState()
      } else {
        this.saveEditorState()
      }
    },
    handleCompiled() {
      this.isCompiled = true
    },
    handleError(error: unknown) {
      this.logs.push(error)
    },
    handleExit() {
      this.isRunning = false
      this.isCompiled = false
      this.isStepping = false
    },
    handleStdout(data: ArrayBuffer | string) {
      if(data instanceof Uint8Array) this.logs.push(new TextDecoder().decode(data))
      else this.logs.push(data)
    },
    handleRegistersChanged(updates: RegistersChanged) {
      Object.entries(updates).forEach(([name, entry]) => {
        const register = this.registers.find(register => register.name === name)
        const { contents, ascii } = entry

        if (register) {
          register.contents = contents
          register.ascii = ascii
        } else {
          this.registers.push({ name, contents, ascii })
        }
      })
    },
    handleMemoryChanged(entry: MemoryChanged) {
      const { address, contents, ascii } = entry
      const index = this.memoryMap.get(address)

      if (index === undefined) {
        this.memoryMap.set(address, this.memory.length)
        this.memory.push(entry)
      } else {
        const currentEntry = this.memory[index]

        if (currentEntry) {
          currentEntry.contents = contents
          currentEntry.ascii = ascii
        }
      }
    },
    toggleSideNav(){
      const newState = !this.isSideNavOpen

      this.iconFolder = newState ? 'folder_open' : 'folder'
      this.isSideNavOpen = newState
    },
    handleColorSchemeChange() {
      const newColorScheme = this.colorScheme === 'dark' ? 'light' : 'dark'

      this.colorScheme = newColorScheme
      this.colorSchemeIcon = `${newColorScheme === 'dark' ? 'light' : 'dark'}_mode`

      document.documentElement.setAttribute('data-mode', newColorScheme)
    },
    async handleProgramChange(e: MouseEvent) {
      const name = (e.target as HTMLButtonElement).dataset.program

      if (!name) return

      this.programName = name

      if (name === 'function') {
        this.setProgram((await import(`./programs/function`)).default)
      }

      else if (name === 'loop') {
        this.setProgram((await import(`./programs/loop`)).default)
      }

      else if (name === 'stack') {
        this.setProgram((await import(`./programs/stack`)).default)
      }

      else if (name === 'pixel_buffer') {
        this.setProgram((await import(`./programs/pixel-buffer`)).default)
      }
    },
    setProgram(program: string) {
      this.handleTabChange(1)

      const doc = program.replace(/\t/g, '  ')

      this.setDoc(doc)
    },
    setEditorState() {
      const codeEditor = this.$refs.codeEditor as any

      if (!codeEditor) return

      const { editorState } = this

      if (editorState) codeEditor.setState(editorState)
    },
    saveEditorState() {
      const codeEditor = this.$refs.codeEditor as any

      if (!codeEditor) return
      this.editorState = codeEditor.getState()
    },
    setDoc(doc: string) {
      const ref = this.$refs.codeEditor as any
      const { editorState } = this

      if(ref) ref.setDoc(doc)

      else if(editorState) editorState.doc = doc
    },
    getDoc() {
      const ref = this.$refs.codeEditor as any

      if(ref) return ref.getDoc()

      const { editorState } = this

      if (editorState) return editorState.doc

      return ''
    },
    async compile() {
      if (this.isRunning) return

      this.logs = []
      this.registers = []
      this.memory = []
      this.memoryMap = new Map()

      await this.$nextTick()

      const source = this.getDoc().replaceAll('\\n', '\n') + '\n'

      emulator_compile(source)
    },
    run() {
      this.isRunning = true

      emulator_run()
    },
    step() {
      this.isStepping = true

      emulator_step()
    },
    exit() {
      this.isRunning = false;
      this.isCompiled = false
      this.isStepping = false

      emulator_stop()
    }
  }
})